import React, { useId, useRef, useState } from 'react';

import { Button, Loading, SystemMessage } from '../..';
import { ENTRA_ID_RETRY_LOADING_MS } from '../../../constantsConfig/constantsConfig';
import type { UseEntraIdLoginReturn } from '../../../hooks/useEntraIdLogin';

interface LoginEntraIdProps {
  isLoading: boolean;
  error: string | null;
  retry: UseEntraIdLoginReturn['signIn'];
}

const LoginEntraId: React.FC<LoginEntraIdProps> = ({ isLoading, error, retry }) => {
  const [isRetrying, setIsRetrying] = useState(false);
  const retryStartedRef = useRef(false);
  const retryTimerRef = useRef(0);
  const mainContentId = useId();
  const loadingStatusId = useId();
  const errorMessageId = useId();
  const showLoading = isLoading || isRetrying || !error;

  const handleRetry = async () => {
    if (retryStartedRef.current) {
      return;
    }
    retryStartedRef.current = true;
    setIsRetrying(true);

    await new Promise<void>((resolve) => {
      retryTimerRef.current = window.setTimeout(resolve, ENTRA_ID_RETRY_LOADING_MS);
    });

    try {
      await retry();
    } finally {
      window.clearTimeout(retryTimerRef.current);
      setIsRetrying(false);
      retryStartedRef.current = false;
    }
  };

  if (showLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background-primary"
        role="main"
        aria-busy
        aria-label="Página de login Entra ID"
      >
        <div className="text-center">
          <div
            id={loadingStatusId}
            role="status"
            aria-live="polite"
            aria-label="Processando autenticação"
          >
            <Loading />
            <p className="text-gray-500 mt-4">Processando autenticação...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-background-primary"
      role="main"
      aria-busy={showLoading}
      aria-label="Página de login Entra ID"
      id={mainContentId}
    >
      <div className="w-full max-w-md px-4">
        {error && (
          <div
            id={errorMessageId}
            className="mb-4"
            role="alert"
            aria-live="assertive"
          >
            <SystemMessage
              message={error}
              type="error"
              show={!!error}
            />
            <div className="mt-4 flex flex-col items-stretch gap-4 w-full">
              <Button
                type="button"
                color="primary"
                fill="outline"
                className="w-full"
                onClick={() => void handleRetry()}
                disabled={showLoading}
                aria-label="Tentar autenticar novamente"
              >
                Tente novamente
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginEntraId;
